import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import PanelController from './PanelController'
import useStore from 'state/knovStore'
import api from 'api/api'
import KnovModal from 'components/shared/KnovModal'
import TrayHost from 'components/shared/TrayHost'
import MediaDock from 'components/shared/MediaDock/MediaDock'
import CryptoPayModal from './shared/CryptoPayModal'
import StoryModal from 'components/shared/StoryModal'
import GlobalNotice from 'components/shared/GlobalNotice'
import ChannelJoinInviteModal from 'components/teams/ChannelJoinInviteModal'
import OrdinalMintStatusListener from 'components/shared/OrdinalMintStatusListener'
import WalletPasskeyRestoreListener from 'components/shared/WalletPasskeyRestoreListener'
import CryptoSwapStatusListener from 'components/shared/CryptoSwapStatusListener'
import LocalSigningShadowHost from 'components/wallets/LocalSigningShadowHost'
import OrdinalStateListener from 'components/shared/OrdinalStateListener'
import LoadingScreen from 'components/shared/LoadingScreen'
import * as Sentry from '@sentry/react'
import { setUserProps } from 'lib/log'
import 'styles/global.scss'
import useInitNotifications from './useInitNotifications'
import { filterForMyStreamsFeature } from 'lib/myStreamsFeature'
import { QueryClientProvider } from '@tanstack/react-query'
import queryClient from 'api/queryClient'
import * as cache from 'state/cache'
import * as localForage from 'localforage'
import useUserChannel from 'state/channels/useUserChannel'
import { DEFAULT_UPVALUE_SATS, DEFAULT_BOOST_SATS, DEFAULT_BSV_UNIT, USD_UNIT } from 'lib/bsv-util'
import { PanelState } from 'state/PanelState'
import { openPlacementIntentForFilter } from 'lib/threadOpenPlacement'
import { BsvWalletType } from 'wallets/wallets'
import { initWallet } from 'wallets/wallets'
import { isMobile } from 'lib/device'
import { useBsvExchangeRate, usdToSats } from 'api/useBsvExchangeRate'
import { apiModelToCamel } from 'state/aiModels'
import { apiActionDefinitionsToCamel, buildActionDefinitions } from 'state/actionDefinitions'
import { markStartupMetric } from 'lib/startupMetrics'
import { resolveAgentModelSelectionId } from 'lib/agentModels'
import {
    buildInitialPanelsFromSession,
    writePanelSessionSnapshot,
} from 'state/panelSessionPersistence'
import { isLocalCachedQuest } from 'components/panels/questDisplay'
import { MIN_SEED_SATS } from 'lib/seedMinimum'
import { normalizeSimpleMoneyUnit } from 'lib/simpleMoneyToggle'
import {
    getAuthSurfaceForTracking,
    getSignupMethod,
    clearAuthSurface,
} from 'lib/authSurfaceTracking'
import { consumeHelloPromptPending, HELLO_PROMPT_EVENT } from 'lib/signupOnboarding'
import WalletSetupHost from 'components/walletSetup/WalletSetupHost'

window.get = useStore.getState().get
window.set = useStore.getState().set
window.cache = cache
window.localForage = localForage

const HELLO_PROMPT_SHOWN_KEY_PREFIX = 'treechat_hello_prompt_shown_'

function normalizeInitialQuestAgentModels(questAgentModels, agentModelConfig) {
    // `gon` can still carry legacy quest-level model ids from older saved
    // `space_options` records, even though new writes now canonicalize them at
    // the Rails boundary. Normalize once while seeding Zustand so opening an
    // older session does not silently drop its selected model.
    return Object.fromEntries(
        Object.entries(questAgentModels || {}).flatMap(([questId, modelName]) => {
            const normalizedModelName = resolveAgentModelSelectionId(modelName, agentModelConfig)
            return normalizedModelName ? [[questId, normalizedModelName]] : []
        }),
    )
}

function scheduleDeferredStartupTask(name, task) {
    if (typeof window === 'undefined') {
        return () => {}
    }

    const runTask = async () => {
        markStartupMetric(`${name}-started`)

        try {
            await task()
            markStartupMetric(`${name}-complete`)
        } catch (error) {
            console.error(`Error running deferred startup task ${name}:`, error)
            markStartupMetric(`${name}-failed`)
        }
    }

    if (typeof window.requestIdleCallback === 'function') {
        const idleHandle = window.requestIdleCallback(
            () => {
                void runTask()
            },
            { timeout: 1500 },
        )

        return () => {
            if (typeof window.cancelIdleCallback === 'function') {
                window.cancelIdleCallback(idleHandle)
            }
        }
    }

    const timeoutHandle = window.setTimeout(() => {
        void runTask()
    }, 250)

    return () => window.clearTimeout(timeoutHandle)
}

// Inner component that uses hooks
function StateProviderInner({ filter }) {
    const openWalletModal = useStore(state => state.actions.modalActions.openWalletModal)
    let serverFilter = gon.currentUser?.space_options?.filter
    // The People stream was removed; migrate stale saved filters to the
    // following feed.
    if (serverFilter?.people) serverFilter = { following: true, order: 'time' }
    const nonEmptyServerFilter =
        serverFilter && Object.keys(serverFilter).length > 0 ? serverFilter : null
    const defaultFilter = { public: true, order: 'time' }
    filter = filterForMyStreamsFeature(filter || nonEmptyServerFilter || defaultFilter)

    const loadingSpaceId = useStore(state => state.loadingSpaceId)
    const activeSpaceId = useStore(state => state.activeSpaceId)
    const agentModelConfig = useStore(state => state.agentModelConfig)

    const set = useStore.getState().set
    const get = useStore.getState().get

    let middleFilter = filter
    const [didExpoInit, setDidExpoInit] = useState(false)

    let middleQuest

    const exchangeRate = useBsvExchangeRate()

    const normalizedQuestAgentModels = normalizeInitialQuestAgentModels(
        gon.currentUser?.space_options?.quest_agent_models,
        agentModelConfig,
    )

    // Route-derived panel state owns first-paint placement. Install it in the layout phase so
    // a boot deep link cannot paint the placeholder/default panel before its placement intent.
    useLayoutEffect(function initializeStateOnFirstRender() {
        // Build a safe initial panels array that works for both mobile (1 panel) and desktop (3 panels)
        const existingPanels = get().panels?.state || []
        const existingCenter =
            existingPanels.find(p => p && !p.empty) || existingPanels[0] || new PanelState()
        const centerPanelId = existingCenter.panelId
        const numPanels = gon.currentUser ? gon.currentUser?.space_options?.numPanels || 3 : 1
        const panelSessionIdentity = {
            userId: gon.currentUser?.id || null,
            spaceId: activeSpaceId || gon.currentUser?.space_id || gon.KNOVIGATOR_SPACE_ID || null,
        }
        const restoredPanels = buildInitialPanelsFromSession({
            ...panelSessionIdentity,
            filter,
            numPanels,
            isMobile,
            fallbackCenterPanelId: centerPanelId,
        })
        // A boot-time deep link (/quest/x/answer/y) should open already placed on the
        // target, exactly like an in-app reply open. Returns undefined flag-off.
        const bootPlacementIntent = openPlacementIntentForFilter(filter)
        const bootPlacementProps = bootPlacementIntent
            ? { pendingOpenPlacement: bootPlacementIntent, savedScrollTop: 0 }
            : {}
        const initialPanels =
            restoredPanels ||
            (isMobile || numPanels === 1
                ? [
                      new PanelState({
                          panelId: centerPanelId,
                          filter: filter,
                          ...bootPlacementProps,
                      }),
                  ]
                : [
                      new PanelState({ empty: true }),
                      new PanelState({
                          panelId: centerPanelId,
                          filter: filter,
                          ...bootPlacementProps,
                      }),
                      new PanelState({ empty: true }),
                  ])
        const initialCenterIndex = Math.max(
            0,
            initialPanels.findIndex(panel => panel.filter === filter && !panel.empty),
        )
        const initialActiveIndex =
            isMobile || numPanels === 1 ? initialCenterIndex : Math.max(0, initialCenterIndex - 1)

        const savedSidebarPreference = gon.currentUser?.space_options?.onePanelSideBar
        let initialShowSidebar = false
        if (!gon.currentUser) {
            initialShowSidebar = !isMobile
        } else if (!isMobile && numPanels === 1) {
            initialShowSidebar = savedSidebarPreference ? savedSidebarPreference === 'open' : true
        }

        const savedUpvalueUsd = Number(gon.currentUser?.space_options?.upvalueUsd)
        const initialUpvalueUsd =
            Number.isFinite(savedUpvalueUsd) && savedUpvalueUsd > 0 ? savedUpvalueUsd : null
        const currentUserFeatures = gon?.currentUser?.features || []
        const initialBsvUnit = normalizeSimpleMoneyUnit(
            gon.currentUser?.space_options?.bsvUnit || DEFAULT_BSV_UNIT,
        )
        const savedUpvalueSats = gon.currentUser?.space_options?.upvalueSats || DEFAULT_UPVALUE_SATS
        const initialUpvalueSats =
            initialBsvUnit === USD_UNIT && initialUpvalueUsd != null && exchangeRate
                ? usdToSats(initialUpvalueUsd, exchangeRate) || savedUpvalueSats
                : savedUpvalueSats
        const savedSeedUsd = Number(gon.currentUser?.space_options?.seedUsd)
        const initialSeedUsd =
            Number.isFinite(savedSeedUsd) && savedSeedUsd > 0 ? savedSeedUsd : null
        const savedSeedSats = Number(gon.currentUser?.space_options?.seedSats)
        const configuredSeedSats =
            Number.isFinite(savedSeedSats) && savedSeedSats > 0 ? savedSeedSats : MIN_SEED_SATS
        const initialSeedSats =
            initialBsvUnit === USD_UNIT && initialSeedUsd != null && exchangeRate
                ? usdToSats(initialSeedUsd, exchangeRate) || configuredSeedSats
                : configuredSeedSats

        const initialState = {
            numPanels: numPanels,
            panels: {
                ...get().panels,
                state: initialPanels,
            },
            activeIndex: initialActiveIndex,
            centerIndex: initialCenterIndex,

            selectedAnswers: {},

            signalType: 'upvalue',
            upvalueSats: initialUpvalueSats,
            upvalueUsd: initialUpvalueUsd,
            seedSats: initialSeedSats,
            seedUsd: initialSeedUsd,
            boostSats: gon.currentUser?.space_options?.boostSats || DEFAULT_BOOST_SATS,
            bsvUnit: initialBsvUnit,
            effectEnabled: gon.currentUser?.space_options?.effectEnabled !== false, // Default to true if not set
            userSpaces: [],
            userStreams: [],
            userFeatures: currentUserFeatures,
            questAgentModels: normalizedQuestAgentModels,
            // Left Panel.
            activeHistoryTab: gon.currentUser?.options?.['activeHistoryTab'] || 'history',
            starredQuestIds:
                gon.currentUser?.starred_quest_ids?.reduce(
                    (acc, id) => ({ ...acc, [id]: true }),
                    {},
                ) || {},
            pinnedTeamIds:
                gon.currentUser?.pinned_team_ids?.reduce(
                    (acc, id) => ({ ...acc, [id]: true }),
                    {},
                ) || {},
            questHistory: [],
            // Middle Panel.
            middleFilter,
            middleQuest: middleQuest || null,
            middleQuests: [],
            middleCount: -1, //props.middleCount, // Could be quest or notification count.
            numNoti: filter.numNoti || 0,
            // Right Panel.
            childQuests: filter.childQuests,
            rightQuery: filter.rightQuery,
            rightParentAnswer: filter.rightParentAnswer,
            rightCount: filter.rightCount,

            filterUser: null,
            users: null,
            publicTeams: null,
            knovigatorSpace: null,
            middlePage: 1,
            rightPage: 1,
            //isModalOpen: false,
            isStoryOpen: false,
            publicUser: filter.publicUser,
            showSidebar: initialShowSidebar,
            closeSidebarOnBlur: false,
            closeSidebarOnSwipe: isMobile,
            agentReplyPendingStatus: {}, // list of quest ids that are waiting for an AI reply
            currentUserBsvWalletType: BsvWalletType.SHUALLET,
            answerJobs: {},
            aiModels: [],
            exchangeRate: exchangeRate,
        }

        const doInitMobileNotifications = async () => {
            const expo_noti_token = window.EXPO_PUSH_TOKEN
            const { id: userId } = gon?.currentUser
            if (expo_noti_token && userId) {
                await api.updateUser(userId, {
                    expo_noti_token,
                })
                console.log('Set mobile notification token for user!')
            }
            // alert(
            //     `is_mobile: ${window.KNOVIGATOR_IS_MOBILE}. Set mobile notification token for user!
            //     token = '${expo_noti_token}'`,      s
            // )
        }

        if (window.KNOVIGATOR_IS_MOBILE && !didExpoInit) {
            // give outer expo layer time to inject the expo token
            setTimeout(() => {
                doInitMobileNotifications()
                setDidExpoInit(true)
            }, 5000)
        }

        set(initialState)
        markStartupMetric('state-provider-mounted', {
            hasCurrentUser: Boolean(gon?.currentUser),
            initialFilter: filter,
        })

        // Seed normalized user cache from gon so stale embedded user objects
        // (e.g. from cached Answer JSON) get overridden immediately on reload.
        try {
            if (gon?.currentUser?.id) {
                cache.cacheUser(gon.currentUser, true)
                queryClient.setQueryData(['user', String(gon.currentUser.id)], gon.currentUser)
            }
        } catch (_) {}

        // Set Amplitude user property for current panel count
        try {
            setUserProps({ num_panels: numPanels })
        } catch (_) {}
    }, [])

    useEffect(() => {
        const helloAnalyticsContext = triggerSource => ({
            auth_surface: getAuthSurfaceForTracking(),
            signup_method: getSignupMethod(),
            trigger_source: triggerSource,
            source: 'new_user_hello_message',
            prompt: 'Hello World',
            action_tag: 'flux2',
        })
        const postHello = context => {
            window.logEv('CLICK_NEW_USER_HELLO_MESSAGE_POST', context)
            api.postHelloMessage(context)
                .catch(error => {
                    console.error('Error posting hello message:', error)
                })
                .finally(() => {
                    clearAuthSurface()
                })
        }

        // Also asked again when the new-wallet setup closes (WalletSetupHost).
        const showHelloPrompt = () => {
            const currentUser = gon?.currentUser
            if (!currentUser?.id || window.location.pathname !== '/') return
            if (!consumeHelloPromptPending(currentUser.id)) return

            const helloPromptShownKey = `${HELLO_PROMPT_SHOWN_KEY_PREFIX}${currentUser.id}`
            const helloPromptShown = localStorage.getItem(helloPromptShownKey)
            if (helloPromptShown) return

            const helloMessageAnalyticsContext = helloAnalyticsContext('direct_signup')

            window.logEv('VIEW_NEW_USER_HELLO_MESSAGE_PROMPT', helloMessageAnalyticsContext)
            localStorage.setItem(helloPromptShownKey, 'true')

            const modalActions = useStore.getState().actions.modalActions
            modalActions.openConfirmModal(
                <div>
                    <strong>Post First, Earn Upvalue</strong>
                    <br />
                    <br />
                    You do not need coins to get started. Post "Hello World" with an AI-generated
                    image so other users can start rewarding your content.
                </div>,
                'Hello World',
                () => postHello(helloMessageAnalyticsContext),
                {
                    cancelMessage: 'Maybe Later',
                    onCancel: () => {
                        window.logEv('SKIP_NEW_USER_HELLO_MESSAGE', {
                            ...helloMessageAnalyticsContext,
                            skip_action: 'maybe_later',
                        })
                        clearAuthSurface()
                    },
                    onDismiss: clearAuthSurface,
                    modalKey: 'new-user-hello',
                },
            )
        }
        showHelloPrompt()
        window.addEventListener(HELLO_PROMPT_EVENT, showHelloPrompt)
        return () => {
            window.removeEventListener(HELLO_PROMPT_EVENT, showHelloPrompt)
        }
    }, [])

    useEffect(
        function persistPanelSession() {
            const panelSessionIdentity = {
                userId: gon.currentUser?.id || null,
                spaceId:
                    activeSpaceId || gon.currentUser?.space_id || gon.KNOVIGATOR_SPACE_ID || null,
            }
            let timeoutId

            const deriveCenterIndex = state => {
                const panels = state.panels?.state || []
                const onePanel = isMobile || state.numPanels === 1
                let centerIndex =
                    typeof state.centerIndex === 'number'
                        ? state.centerIndex
                        : onePanel
                        ? state.activeIndex || 0
                        : (state.activeIndex || 0) + 1

                if (!onePanel && panels[centerIndex]?.empty) {
                    if (panels[centerIndex + 1] && !panels[centerIndex + 1].empty) centerIndex += 1
                    else if (panels[centerIndex - 1] && !panels[centerIndex - 1].empty)
                        centerIndex -= 1
                }

                return Math.max(0, Math.min(centerIndex, Math.max(0, panels.length - 1)))
            }

            const persistNow = () => {
                const state = useStore.getState()
                const panels = state.panels?.state || []
                if (!panels.some(panel => panel && !panel.empty && panel.filter)) return

                const centerIndex = deriveCenterIndex(state)
                writePanelSessionSnapshot(panelSessionIdentity, {
                    numPanels: state.numPanels,
                    activeIndex:
                        isMobile || state.numPanels === 1
                            ? centerIndex
                            : Math.max(0, centerIndex - 1),
                    centerIndex,
                    panels,
                    isMobile,
                    shouldPersistPanel: panel => {
                        const questId = panel?.filter?.questId
                        if (!questId) return true

                        const quest = cache.getCachedQuest(questId)
                        return !!quest && !isLocalCachedQuest(quest)
                    },
                })
            }

            const schedulePersist = () => {
                if (timeoutId) window.clearTimeout(timeoutId)
                timeoutId = window.setTimeout(persistNow, 150)
            }

            schedulePersist()
            const unsubscribe = useStore.subscribe((state, previousState) => {
                if (
                    state.panels?.state !== previousState.panels?.state ||
                    state.activeIndex !== previousState.activeIndex ||
                    state.centerIndex !== previousState.centerIndex ||
                    state.numPanels !== previousState.numPanels
                ) {
                    schedulePersist()
                }
            })
            window.addEventListener('pagehide', persistNow)

            return () => {
                if (timeoutId) window.clearTimeout(timeoutId)
                unsubscribe()
                window.removeEventListener('pagehide', persistNow)
            }
        },
        [activeSpaceId],
    )

    useEffect(function getKnovigatorSpaceOnMount() {
        return scheduleDeferredStartupTask('load-knovigator-space', async () => {
            const defaultSpaceId = gon.KNOVIGATOR_SPACE_ID
            if (!defaultSpaceId) return

            let knovigatorSpace = null
            try {
                knovigatorSpace = await api.getSpace(defaultSpaceId)
            } catch (_) {
                // A 404 can render a non-JSON body; treat any failure as "did not resolve".
            }
            if (knovigatorSpace?.id) {
                set({ knovigatorSpace })
                return
            }

            // The env-configured default space doesn't resolve on this deployment (deleted
            // space, reset DB, copy-pasted env). If it leaked into activeSpaceId via the
            // pre-login bootstrap, every space-scoped request 403s into a silently empty
            // feed — fall back to the user's own space instead.
            const fallbackSpaceId = gon.currentUser?.space_id
            if (fallbackSpaceId && useStore.getState().activeSpaceId === defaultSpaceId) {
                console.warn(
                    '[space] KNOVIGATOR_SPACE_ID did not resolve; falling back to user space',
                    { defaultSpaceId, fallbackSpaceId },
                )
                window.__KNOV_PENDING_ACTIVE_SPACE_ID__ = fallbackSpaceId
                useStore.getState().set({ activeSpaceId: fallbackSpaceId })
            }
        })
    }, [])

    useEffect(() => {
        // Allow external redirects (e.g. checkout flows) to re-open the Wallet modal.
        try {
            const url = new URL(window.location.href)
            if (url.searchParams.get('open_wallet') === '1') {
                openWalletModal()
            }
        } catch (_) {}
    }, [openWalletModal])

    useEffect(
        function getTeams() {
            if (gon.currentUser && activeSpaceId)
                api.getTeams({ space_id: activeSpaceId }).then(teams => {
                    set({ userStreams: teams })
                })
        },
        [gon.currentUser, activeSpaceId],
    )

    useEffect(
        function getUserSpaces() {
            if (gon.currentUser)
                api.getSpaces().then(spaces => {
                    set({ userSpaces: spaces })
                })
        },
        [gon.currentUser],
    )

    // The space user list loads on demand (state/spaceUsers), not at boot.

    useEffect(
        function getPublicTeamsOnMount() {
            // For logged-out users, don't filter by space_id to get all public teams
            const params = gon.currentUser ? { space_id: activeSpaceId } : {}
            api.getPublicTeams(params)
                .then(publicTeams => {
                    set({ publicTeams })
                })
                .catch(err => {
                    console.error('Error loading public teams:', err)
                    set({ publicTeams: [] })
                })
        },
        [activeSpaceId, gon.currentUser],
    )

    useEffect(function getAiModelsOnMount() {
        if (!gon?.currentUser) return
        return scheduleDeferredStartupTask('load-ai-models', async () => {
            const aiModelsApi = await api.getAiModels()
            // convert snake_case api models to camelCase before storing
            const aiModels = aiModelsApi.map(apiModelToCamel)
            const actionDefinitionsApi = await api.getActionDefinitions()
            const actionDefinitions = apiActionDefinitionsToCamel(actionDefinitionsApi, aiModels)
            set({
                aiModels,
                actionDefinitions:
                    actionDefinitions.length > 0
                        ? actionDefinitions
                        : buildActionDefinitions(aiModels),
            })
        })
    }, [])

    useInitNotifications()

    useEffect(() => {
        if (!gon?.currentUser?.id) return

        return scheduleDeferredStartupTask('bootstrap-wallet', async () => {
            const wallet = initWallet(BsvWalletType.SHUALLET)

            try {
                await wallet?.authenticate?.()
            } catch (err) {
                console.error('Error initializing wallet:', err)
            }

            try {
                await api.ensureBsvWallet()
            } catch (err) {
                console.error('Error ensuring wallet bootstrap data:', err)
            }
        })
    }, [gon?.currentUser?.id])

    useUserChannel()

    useEffect(() => {
        const nextState = { exchangeRate }
        const { bsvUnit, upvalueUsd, seedUsd } = useStore.getState()
        const nextUpvalueSats =
            bsvUnit === USD_UNIT && upvalueUsd != null ? usdToSats(upvalueUsd, exchangeRate) : null
        const nextSeedSats =
            bsvUnit === USD_UNIT && seedUsd != null ? usdToSats(seedUsd, exchangeRate) : null

        if (nextUpvalueSats != null) {
            nextState.upvalueSats = nextUpvalueSats
        }
        if (nextSeedSats != null) {
            nextState.seedSats = nextSeedSats
        }

        set(nextState)
    }, [exchangeRate])

    return (
        <>
            {loadingSpaceId ? (
                <LoadingScreen spaceId={loadingSpaceId || activeSpaceId} />
            ) : (
                <PanelController />
            )}
            <KnovModal />
            <TrayHost />
            <MediaDock />
            <CryptoPayModal />
            <StoryModal />
            <GlobalNotice />
            {window.gon?.currentUser?.id && <WalletSetupHost />}
            {window.gon?.currentUser?.id && <ChannelJoinInviteModal />}
            {window.gon?.currentUser?.id && <OrdinalMintStatusListener />}
            {window.gon?.currentUser?.id && <WalletPasskeyRestoreListener />}
            {window.gon?.currentUser?.id && <CryptoSwapStatusListener />}
            {window.gon?.currentUser?.id &&
                (window.gon?.localSigningShadow ||
                    window.gon?.localSigningShareRecovery ||
                    window.gon?.localSigningShadowRevokedDevices?.length > 0) && (
                    <LocalSigningShadowHost />
                )}
            {/* Not gated on a signed-in user: the public lane keeps a sold
                ordinal's owner honest for guests reading the wall too. */}
            <OrdinalStateListener />
            {!isMobile && <ReactQueryDevtools initialIsOpen={false} />}
        </>
    )
}

// Outer component that provides React Query context
export default function StateProvider({ filter }) {
    return (
        <Sentry.ErrorBoundary fallback={<div>An error has occurred</div>}>
            <QueryClientProvider client={queryClient}>
                <StateProviderInner filter={filter} />
            </QueryClientProvider>
        </Sentry.ErrorBoundary>
    )
}
